import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { OptionType } from 'components/CustomSelect/types';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import SelectValueViewer from 'components/SelectValueViewer';

const EntityValueViewer = (props: any) => {
  const { cell } = props;
  const [availableEntities, setAvailableEntities] = useState<OptionType[]>([]);
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const { onCellsChanged } = useContext(FeaturedSpreadsheetContext) as any;

  useEffect(() => {
    if (!companyInfo) {
      return;
    }
    const tempEntities = [
      {
        id: String(companyInfo.id),
        value: String(companyInfo.id),
        label: companyInfo.name || '',
      },
      ...(companyInfo?.child_companies || []).map((child: any) => ({
        id: String(child.company_id),
        value: String(child.company_id),
        label: child.company_name || '',
      })),
    ];

    if (availableEntities.length === 0) {
      setAvailableEntities(tempEntities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo]);

  useEffect(() => {
    if (!cell.value && companyInfo) {
      onCellsChanged([{ cell, value: companyInfo.id }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell.value]);

  const isChildCompany = useMemo(() => {
    if (!companyInfo?.child_companies || !cell?.value) {
      return false;
    }
    return (companyInfo.child_companies as any[]).some(
      child =>
        (`${child.company_id}` === `${cell.value}` || child.company_name === cell.value) &&
        `${child.company_id}` !== `${companyInfo.id}`
    );
  }, [companyInfo, cell?.value]);

  if (availableEntities.length === 0) {
    return null;
  }

  if (!isChildCompany) {
    return <SelectValueViewer {...props} options={availableEntities} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <SelectValueViewer {...props} options={availableEntities} />
      </Box>
      <Tooltip
        title="Related Entity Allocation: Proceeds and economics flow through the related entity's allocation and cap table."
        arrow
      >
        <Chip
          label="Related Entity Allocation"
          size="small"
          color="info"
          variant="outlined"
          data-testid="related-entity-allocation-badge"
          sx={{
            height: 18,
            fontSize: '0.625rem',
            fontWeight: 600,
            mx: 0.5,
            flexShrink: 0,
            cursor: 'help',
            whiteSpace: 'nowrap',
            '& .MuiChip-label': {
              px: 0.5,
              py: 0,
            },
          }}
        />
      </Tooltip>
    </Box>
  );
};

export default EntityValueViewer;
